import { useTranslation } from "react-i18next"

import type { IDateFormat } from "@/models/date/date"
import { EMPTY_DATA_PLACEHOLDER } from "@/utils/constants"
import { translateDate } from "@/utils/date"
import { DateTime } from "luxon"

export type DateRangeBound = DateTime | string | null | undefined

export interface FormatDateRangeCallOptions {
  showPlaceholder?: boolean
  /**
   * Zone to display. If omitted, a DateTime keeps its own zone; strings use Luxon's default zone.
   */
  timeZone?: string
}

interface UseFormatDateRangeValue {
  formatDateRange: (
    start: DateRangeBound,
    end: DateRangeBound,
    options?: FormatDateRangeCallOptions
  ) => string
}

const toDateTime = (
  value: DateRangeBound,
  timeZone?: string
): DateTime | null => {
  if (value == null || value === "") {
    return null
  }

  const zone = timeZone?.trim() ?? undefined

  if (typeof value !== "string") {
    const date = zone ? value.setZone(zone) : value
    return date.isValid ? date : null
  }

  const trimmed = value.trim()
  if (trimmed === "") {
    return null
  }

  const date = zone
    ? DateTime.fromISO(trimmed, { zone })
    : DateTime.fromISO(trimmed)

  return date.isValid ? date : null
}

/**
 * Formats a date range as a localized string.
 * A bound is an ISO string or a DateTime. One bound shows that date.
 * Both bounds join with an en dash and a space on each side.
 * Neither bound is "--" unless showPlaceholder is false.
 */
export const useFormatDateRange = (
  format?: IDateFormat
): UseFormatDateRangeValue => {
  const { i18n } = useTranslation()

  const formatDateRange = (
    start: DateRangeBound,
    end: DateRangeBound,
    options?: FormatDateRangeCallOptions
  ): string => {
    const startDate = toDateTime(start, options?.timeZone)
    const endDate = toDateTime(end, options?.timeZone)
    const only = startDate ?? endDate

    if (startDate != null && endDate != null) {
      return `${translateDate(i18n, startDate, format)} – ${translateDate(i18n, endDate, format)}`
    }

    if (only != null) {
      return translateDate(i18n, only, format)
    }

    return options?.showPlaceholder === false ? "" : EMPTY_DATA_PLACEHOLDER
  }

  return { formatDateRange }
}
